import FormElementMixin from '../mixins/FormElementMixin.js';
import MatrixFormTrigger from "../triggers/MatrixFormTrigger";
import ListWidgetMixin from "../../mixins/ListWidgetMixin";
import {deepEqual} from "../../../utils/helpers/object-helpers";

const AkMatrix = {
    mixins: [FormElementMixin, ListWidgetMixin],
    data() {
        return {
            currentValues: [],
            dragging: false,
            refreshWidgetIndex: 0
        }
    },
    props: {
        controls: {
            type: Array,
            required: true,
        },
        controlErrors: {
            type: Array|Object,
        },
        sortingEnabled: {
            required: false,
        },
        addLineEnabled: {
            required: false,
            default: true,
        },
        deleteLineEnabled: {
            required: false,
            default: true,
        },
        groupsEnabled: {
            required: false,
            default: false,
        },
        groupControl: {
            type: Object,
            required: false,
        },
        widths: {
            type: String,
            required: false
        },
        noPadding: {
            type: Boolean,
        },
        noPaddingTop: {
            type: Boolean,
        },
        noPaddingBottom: {
            type: Boolean,
        },
        hideLabels: {
            type: Boolean,
        },
        addNewLineOnTab: {
            type: Boolean,
        }
    },
    watch: {
        value(newValue, oldValue) {
            if( ! this.$deepEqual(newValue, oldValue)) {
                this.localValue = newValue;
                this.currentValues = newValue;
            }
        },
        currentValues: {
            handler() {
                let values = [...this.currentValues];

                if( ! this.$deepEqual(this.value, values)) {
                    this.currentValue = values;
                }
            },
            deep: true
        }
    },
    computed: {
        /**
         * Get the visible controls
         * @return {*[]}
         */
        visibleControls() {
            return this.controls.filter(control => !control.formControl.hidden);
        },
        widthsCssVariable() {
            if ( ! this.widths) {
                return null;
            }
            return '--matrix-grid-auto-columns:' + JSON.parse(this.widths).join(' ') + ';';
        }
    },
    methods: {
        /**
         * Add a new row to the matrix
         */
        addRow() {
            this.addValueObject();
        },
        /**
         * Add a new row to given group
         */
        addRowToGroup(group) {
            group.items.push(this.getValueObject());
        },
        /**
         * Remove an item from the values by key
         * @param key
         */
        removeRow(key) {
            this.currentValues.splice(key, 1);
        },
        /**
         * Remove an item from the group
         * @param key
         */
        removeRowFromGroup(groupkey,key) {
            this.currentValues[groupkey].items.splice(key, 1);
            this.setNewValueId(this.currentValues[groupkey]);
        },
        /**
         * Remove an group
         * @param key
         */
        removeGroup(groupkey) {
            this.currentValues.splice(groupkey, 1);
        },
        /**
         * We push an extra value row
         */
        addValueObject() {
            this.$nextTick(() => {

                this.currentValues.push(this.getValueObject());
            });
        },
        /**
         * generate the value object depending on the controls given
         * @returns {{}}
         */
        getValueObject() {
            let obj = {
                valueId: Date.now()
            };

            this.controls.forEach(control => {
                // we use the value fo the given formControl in case we have a default value set
                obj[control.id] = JSON.parse(JSON.stringify(control.formControl.value)); // make sure we clone the value
            });

            return obj;
        },
        /**
         * We push an extra group to the value
         */
        addGroupObject() {
            this.$nextTick(() => {
                this.currentValues.push(this.getGroupValue());
            });
        },
        /**
         * Generate a new group object
         * @returns {{valueId: number, items: *[]}}
         */
        getGroupValue() {
            const group = {
                valueId: Date.now(),
                items: []
            };

            if (this.groupControl) {
                group[this.groupControl.id] = JSON.parse(JSON.stringify(this.groupControl.formControl.value));
            }

            group.items.push(this.getValueObject());
            return group;
        },
        /**
         * Generate the widget attributes for the matrix columns
         * @param widget
         * @param row
         * @returns {*&{id, title: null}}
         */
        widgetAttributes(widget, row, group) {
            const currentValue = group !== undefined ?
                    this.currentValues[group]['items'][row][widget.id]:
                    this.currentValues[row][widget.id];

            let attributes = {
                id: this.id,
                ...widget.formControl,
                ...widget.attributes,
                value: currentValue
            };

            if (this.groupsEnabled && !attributes.placeholder) {
                attributes.placeholder = attributes.title;
            }

            attributes.title = null;

            if (! this.controlErrors) {
                return attributes;
            }

            if (this.groupsEnabled && this.controlErrors[group] && this.controlErrors[group] && this.controlErrors[group][row] && this.controlErrors[group][row][widget.id]) {
                attributes = {...attributes, ...this.controlErrors[group][row][widget.id]};
                return attributes;
            }

            if (this.controlErrors[row] && this.controlErrors[row][widget.id]) {
                attributes = {...attributes, ...this.controlErrors[row][widget.id]};
                return attributes;
            }

            return attributes;
        },
        /**
         * Validate the child widgets
         * @param $event
         * @param widgetId
         * @param rowId
         * @param groupId
         */
        validateChildWidget($event, widgetId, rowId, groupId = null) {
            this.$emit('blur', new MatrixFormTrigger(widgetId, rowId, groupId))
        },
        /**
         * Child widget has gotten input
         */
        inputChildWidget() {
            this.$emit('input', this.currentValues);
        },
        /**
         * Set new value id
         * @param object
         */
        setNewValueId(object) {
            object.valueId = Date.now();
        },
        /**
         * Update the refresh widget index so the widgets will be forced to refresh
         */
        triggerRefreshWidget() {
            this.refreshWidgetIndex++;
        },
        /**
         * Check if we need to add a new line
         *
         * @param e
         * @param rowIndex
         * @param controlId
         * @param groupKey
         */
        checkAddNewLine(e, rowIndex, controlId, groupKey = null) {

            // make sure add new line on tab is enabled and we have a tab event
            if ( ! this.addNewLineOnTab) {
                return;
            }

            // Check if we have a keyboard event and it is a tab
            if (e instanceof KeyboardEvent && e.key !== 'Tab') {
                return;
            }

            const widget = this.visibleControls.find(widget => widget.id === controlId);
            // We also allow an input of a autocomoplete to add a new row
            // soif it isent a keyboard event and and doesne't contain ak-autocomplete we stop here
            if ( !(e instanceof KeyboardEvent) && !widget.type.includes('ak-autocomplete')) {
                return;
            }

            if (! this.isLastRowAndWidget(rowIndex, controlId, groupKey)) {
                return;
            }

            // add row to group or just the values
            if (groupKey !== null) {
                this.addRowToGroup(this.currentValues[groupKey]);
            } else {
                this.addRow();
            }
        },
        /**
         * Check if we have the last Widget based on the given params
         *
         * @param rowIndex
         * @param controlId
         * @param groupKey
         * @return {boolean}
         */
        isLastRowAndWidget(rowIndex, controlId, groupKey = null) {
            let values = this.currentValues;
            // If we have a group we get the values of the group
            if (groupKey !== null) {
                values = this.currentValues[groupKey].items;
            }

            // check if event was triggered in the last widget
            const isLastWidget = (rowIndex + 1) === values.length;
            if (! isLastWidget) {
                return false;
            }

            // check if last control of the row
            return Object.values(this.visibleControls).pop().id === controlId;
        },
        dragStart() {
            this.dragging = true;
        },
        dragEnd() {
            this.dragging = false;
        }
    },
    created() {
        if ( this.value && this.value.length) {
            // if we have values we assume it is in the correct format
            this.currentValues = this.value;
        } else {
            // we add a value object for 1 row because default we show 1 row
            if( this.groupsEnabled) {
                this.addGroupObject();
            } else {
                this.addValueObject();
            }
        }
    }
};

Vue.asyncComponent('ak-matrix', AkMatrix, 'form/controls/ak-matrix.html');
export default AkMatrix;